import { Component, computed, DestroyRef, ElementRef, inject, Signal, signal, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize, map } from 'rxjs/operators';
import { BaseSidebarComponent, SIDEBAR_DATA, SidebarRef, TooltipDirective } from '@cumlaude/shared-components-overlays';
import { RestService } from '@cumlaude/shared-services';
import { UrlService } from '../../services/url.service';
import { AutofocusDirective } from '@cumlaude/shared-components-inputs';
import { GroeperingService } from '../../services/groepering.service';
import { UserMenuService } from '../../services/user-menu.service';
import { QueryParamStateService, sortCodec } from '../../services/query-param-state.service';
import { FilterService } from '../../services/filter.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { DASHBOARD_OPTIONS, DashboardOptionParam, findDashboardOptionsByName } from '@cumlaude/shared-dashboard-registry';
import { AssistantService, AssistantStreamError } from '../../services/assistant.service';
import { UserService } from '../../services/user.service';
import { GtmService } from '../../services/gtm.service';
import { GtmEventName } from '../../services/gtm-events';
import {
	RAIChatConversatie,
	ConversationTurn,
	ConversationTurnWithFeedback,
	AssistantStreamEvent,
	ModelCallDebug,
	ModelResponse,
	ModelSuggestion,
} from '@cumlaude/service-contract';
import { firstValueFrom } from 'rxjs';
import { Dialog } from '@angular/cdk/dialog';
import { ChatFeedbackComponent } from './chat-feedback.component';
import { ChatTurnsComponent, DashboardURLComponents, getDashboardURLComponents } from '@cumlaude/shared-components-features';
import { isSchooljaarFilterVan, SCHOOLJAAR_HISTORIE_FILTERS, SchooljaarHistorieFilterValue, schooljarenVoorHistorie } from '@cumlaude/shared-utils';
import { FilterName } from '../../services/filter-name';
import { ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { ChatHistoryDialogComponent } from './chat-history-dialog.component';
import { IconComponent } from '@cumlaude/shared-components-icons';

const menuPositions: ConnectedPosition[] = [
	{
		originX: 'start',
		originY: 'bottom',
		overlayX: 'start',
		overlayY: 'top',
	},
	{
		originX: 'end',
		originY: 'bottom',
		overlayX: 'end',
		overlayY: 'top',
	},
];

export type ChatSidebarData = {
	chatId: number | undefined;
	// Als true, dan bekijken we de chat in inspect mode: readonly en details zichtbaar.
	inspect?: boolean;
};

@Component({
	selector: 'app-chat-sidebar',
	templateUrl: './chat-sidebar.component.html',
	styleUrls: ['./chat-sidebar.component.scss'],
	imports: [
		BaseSidebarComponent,
		FormsModule,
		AutofocusDirective,
		TooltipDirective,
		ChatFeedbackComponent,
		ChatTurnsComponent,
		ChatHistoryDialogComponent,
		IconComponent,
	],
})
export class ChatSidebarComponent {
	private readonly destroyRef = inject(DestroyRef);
	private readonly assistantService = inject(AssistantService);
	private readonly userService = inject(UserService);
	private readonly gtm = inject(GtmService);
	private readonly urlService = inject(UrlService);
	private readonly groeperingService = inject(GroeperingService);
	private readonly filterService = inject(FilterService);
	private readonly userMenuService = inject(UserMenuService);
	private readonly qp = inject(QueryParamStateService);
	protected readonly sidebarRef = inject<SidebarRef>(SidebarRef);
	private readonly restService = inject(RestService);
	private readonly dialog = inject(Dialog);
	private readonly overlay = inject(Overlay);
	protected readonly data = inject<ChatSidebarData>(SIDEBAR_DATA) ?? { chatId: undefined };

	// Referentie naar de textarea voor het chatbericht, zodat we deze kunnen focussen na het verzenden van een bericht
	@ViewChild('chatTextarea') chatTextarea?: ElementRef<HTMLTextAreaElement>;

	@ViewChild('feedbackDialog')
	feedbackDialog!: TemplateRef<string>;

	@ViewChild('historyDialog')
	historyDialog!: TemplateRef<string>;

	@ViewChild('historyButton', { read: ElementRef })
	historyButton!: ElementRef;

	sortOrder = this.qp.signal('sortOrder');

	private readonly optionValues: Record<DashboardOptionParam, Signal<string | undefined>> = {
		...(Object.fromEntries(
			DASHBOARD_OPTIONS.map((option) => option.param)
				.filter(
					(param): param is Exclude<DashboardOptionParam, 'sortOrder' | 'aanwezigheiduitsplitsing'> =>
						param !== 'sortOrder' && param !== 'aanwezigheiduitsplitsing'
				)
				.map((param) => [param, this.qp.encodedSignal(param)])
		) as Record<Exclude<DashboardOptionParam, 'sortOrder' | 'aanwezigheiduitsplitsing'>, Signal<string | undefined>>),
		sortOrder: computed(() => {
			const currentSortOrder = this.sortOrder();
			return currentSortOrder?.active && currentSortOrder?.direction ? (sortCodec.encode(currentSortOrder) as string) : undefined;
		}),
		aanwezigheiduitsplitsing: this.qp.signal_aanwezigheiduitsplitsing(),
	};

	private readonly filter = toSignal(
		this.filterService.getFilledFilters().pipe(
			map((filters) => {
				const vervangen = SCHOOLJAAR_HISTORIE_FILTERS.filter((f) => filters.some(([naam]) => naam === f.historie));
				return filters
					.filter(([naam]) => !vervangen.some((f) => isSchooljaarFilterVan(f, naam)))
					.flatMap(([naam, value]) => this.toAssistantFilters(naam, value));
			})
		)
	);

	/**
	 * De volledige conversatie, die bestaat uit rondes (turns) met een vraag/antwoord-combinatie.
	 * Na het stellen van een vraag bevat de laatste ronde alleen de vraag van de gebruiker.
	 * Zodra het antwoord van de AI is ontvangen, wordt de laatste ronde bijgewerkt met het antwoord.
	 * De gebruiker kan feedback geven per ronde. De volledige conversatie, inclusief feedback
	 * wordt gepersisteerd in de backend.
	 */
	readonly conversation = signal<RAIChatConversatie>(this.createAIChatConversatie());

	readonly userPrompt = signal('');
	readonly isLoading = signal(false);
	readonly progressMessages = signal<string[]>([]);

	readonly suggestions = computed(() => {
		if (this.isLoading() || this.conversation().turns.length === 0) return [];
		return this.conversation().turns[this.conversation().turns.length - 1].response?.suggestions ?? [];
	});

	constructor() {
		if (this.data?.chatId) {
			firstValueFrom(this.restService.getAIChatConversatie(this.data.chatId)).then((chat) => {
				// Wanneer de chat vanuit de URL geladen wordt, hoeven we niet naar het laatste dashboard te navigeren,
				// omdat we al op dat dashboard zitten.
				this.loadChat(chat, false);
			});
		}
	}

	clearConversation() {
		this.conversation.set(this.createAIChatConversatie());
		this.focusTextarea();
	}

	keyDown(event: KeyboardEvent) {
		if (event.key === 'Enter' && !event.shiftKey) {
			this.sendUserPrompt();
			event.preventDefault();
		} else if (event.key === 'Escape') {
			this.sidebarRef.requestClose();
			event.preventDefault();
		}
	}

	sendUserPrompt() {
		this.sendMessage(this.userPrompt(), 'assistant_user_prompt');
	}

	private sendMessage(prompt: string, gtmEvent: Extract<GtmEventName, 'assistant_user_prompt' | 'assistant_select_suggestion'>) {
		if (!prompt.trim() || this.isLoading()) return;

		this.gtm.pushEvent(gtmEvent);

		// Voeg de nieuwe prompt toe aan de conversatie
		this.conversation.set({ ...this.conversation(), turns: [...this.conversation().turns, this.buildNewConversationTurn(prompt)] });
		this.userPrompt.set('');
		this.isLoading.set(true);

		// Roep de assistent-API aan met de volledige conversatie, inclusief de nieuwe prompt
		this.assistantService
			.answerStream(
				this.conversation().turns.map((turn) => ({ request: turn.request, response: turn.response })),
				this.userService.useNoVOBlockList(),
				this.userService.useNoVSOBlockList()
			)
			.pipe(
				// Sluit de gebruiker de sidebar terwijl het model nog bezig is, dan breekt dit het
				// request af in plaats van het antwoord in een weggegooide component te laten landen.
				takeUntilDestroyed(this.destroyRef),
				finalize(() => {
					// Na het ophalen van het antwoord, reset de loading state en focus de textarea
					this.isLoading.set(false);
					this.progressMessages.set([]);
					this.focusTextarea();
				})
			)
			.subscribe({
				next: (event: AssistantStreamEvent) => {
					if (event.type === 'progress') {
						this.progressMessages.update((msgs) => [...msgs, event.message]);
						return;
					}
					// Splitsen, niet doorgeven: alles wat in `response` blijft staan gaat bij een
					// vervolgvraag als antwoord terug naar het model.
					const { debug, ...response } = event.response;
					this.updateConversationWithResponse(response, debug);
				},
				error: (error) => {
					console.error('Error getting AI response:', error);
					// Ook een mislukte aanroep heeft tokens gekost — bij een uitgelopen tool-loop zelfs
					// het meest van alles. De backend stuurt die informatie mee in de foutbody; bij een
					// netwerkfout is er geen body en blijft alleen de serverlog over.
					this.updateConversationWithResponse(
						{
							explanation: 'Er is een fout opgetreden bij het ophalen van het antwoord. Probeer het later opnieuw.',
							subject: 'Foutmelding',
						},
						error instanceof AssistantStreamError ? error.fout?.debug : undefined
					);
				},
			});
	}

	/**
	 * Geeft een historiefilter terug als het gewone schooljaarfilter met de schooljaren die het oplevert; het omgekeerde van `vertaalNaarHistorie`.
	 */
	private toAssistantFilters(filterName: FilterName, value: unknown): { field: string; values: string[] }[] {
		const inputValue = this.filterService.configs[filterName]!.stateToInput(value);
		const historie = SCHOOLJAAR_HISTORIE_FILTERS.find((f) => f.historie === filterName || f.aangepast === filterName);
		if (historie) {
			const schooljaren =
				filterName === historie.historie
					? schooljarenVoorHistorie(inputValue as SchooljaarHistorieFilterValue)
					: (inputValue as string[] | undefined);
			return schooljaren?.length ? [{ field: historie.actueel, values: schooljaren }] : [];
		}

		const values = (Array.isArray(inputValue) ? inputValue : [inputValue]).map((v) =>
			v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v)
		);
		return [{ field: filterName as string, values }];
	}

	private buildNewConversationTurn(userPrompt: string): ConversationTurn {
		let dashboard = undefined;
		const currentDashboard = this.userMenuService.dashboard();
		if (currentDashboard) {
			let grouping = undefined;
			const huidigeGroeperingen = this.groeperingService.huidigeGroeperingen();
			if (huidigeGroeperingen && huidigeGroeperingen.length > 0) grouping = huidigeGroeperingen.map((groepering) => groepering.join('.'));

			const filter = this.filter();
			const options = Object.fromEntries(
				findDashboardOptionsByName(currentDashboard.fullName).flatMap((option) => {
					const waarde = this.optionValues[option.param]();
					return waarde ? [[option.param, waarde]] : [];
				})
			);

			dashboard = {
				dashboard: currentDashboard.fullName,
				grouping,
				filter,
				options: Object.keys(options).length > 0 ? options : undefined,
			};
		}

		return {
			request: {
				userPrompt,
				dashboard,
			},
		};
	}

	private updateConversationWithResponse(response: ModelResponse, debug?: ModelCallDebug) {
		if (this.conversation().turns.length === 0) {
			console.warn('No conversation turns available to update with response.');
			return;
		}

		const index = this.conversation().turns.length - 1;
		const lastTurn = this.conversation().turns[index];
		const turn = { ...lastTurn, response, debug };
		this.conversation.set({
			...this.conversation(),
			turns: [...this.conversation().turns.slice(0, -1), turn],
			lengte: this.conversation().turns.length,
			// Sla alleen het onderwerp van het eerste antwoord op
			...(!index ? { onderwerp: response.subject } : {}),
		});
		this.saveOrUpdateAIChatConversatie();

		// Als het antwoord een dashboard bevat (en niet alleen een uitleg), ga dan naar het dashboard
		const dashboard = getDashboardURLComponents(response?.dashboard);
		if (dashboard) this.gtm.pushEvent('assistant_auto_dashboard');

		this.goToDashboard(dashboard);
	}

	async giveFeedback(index: number, sentiment: 1 | -1) {
		const updateTurn = (
			index: number,
			updatedTurn: ConversationTurnWithFeedback,
			heeftFeedback = this.conversation().heeftFeedback,
			sentiment = this.conversation().sentiment
		) => {
			this.conversation.set({
				...this.conversation(),
				turns: [...this.conversation().turns.slice(0, index), updatedTurn, ...this.conversation().turns.slice(index + 1)],
				heeftFeedback,
				sentiment,
			});
		};

		const turn = this.conversation().turns[index];
		if (!turn) return;

		updateTurn(index, { ...turn, sentiment });

		const feedback = await firstValueFrom(
			this.dialog.open<string>(this.feedbackDialog, {
				data: {
					sentiment,
					feedback: turn.feedback ?? '',
				},
			}).closed
		);

		if (feedback === undefined) {
			// Geannuleerd
			updateTurn(index, turn);
			return;
		}

		updateTurn(index, { ...turn, feedback, sentiment }, true, sentiment);
		this.saveOrUpdateAIChatConversatie();
	}

	private createAIChatConversatie(): RAIChatConversatie {
		const account = this.userService.account()!;
		return {
			$type: 'aichat.RAIChatConversatie',
			instelling: account.instelling,
			account: account,
			onderwerp: '',
			created: new Date().toISOString(),
			lastModified: new Date().toISOString(),
			heeftFeedback: false,
			turns: [],
			lengte: 0,
			verwijderd: false,
		};
	}

	private async saveOrUpdateAIChatConversatie(): Promise<RAIChatConversatie> {
		const conversatie = this.conversation();
		if (this.data?.inspect) return conversatie;

		try {
			conversatie.lastModified = new Date().toISOString();
			if (conversatie.id) {
				return await firstValueFrom(this.restService.putAIChatConversatie(conversatie));
			} else {
				const result = await firstValueFrom(this.restService.postAIChatConversatie(conversatie));
				this.conversation.set({ ...this.conversation(), id: result.id });
				return result;
			}
		} catch (error) {
			console.error('Error saving AI chat conversation:', error);
			throw error;
		}
	}

	focusTextarea() {
		setTimeout(() => {
			this.chatTextarea?.nativeElement.focus();
		});
	}

	goToDashboardClick(dashboard: DashboardURLComponents) {
		this.gtm.pushEvent('assistant_select_dashboard');
		this.goToDashboard(dashboard);
	}

	goToDashboard(dashboard: DashboardURLComponents | undefined) {
		if (dashboard) {
			this.urlService.redirect(dashboard.url, dashboard.queryParams, 'replace');
		}
	}

	selectSuggestion(suggestion: ModelSuggestion) {
		if (this.isLoading() || !suggestion) return;

		this.sendMessage(suggestion.prompt, 'assistant_select_suggestion');
		this.focusTextarea();
	}

	openHistory() {
		this.dialog.open(this.historyDialog, {
			positionStrategy: this.overlay.position().flexibleConnectedTo(this.historyButton).withPositions(menuPositions),
		});
	}

	selectHistoryChat(chat: RAIChatConversatie | null) {
		if (chat) this.gtm.pushEvent('assistant_history_select_chat');

		this.loadChat(chat);
	}

	loadChat(chat: RAIChatConversatie | null, goToLastDashboard: boolean = true) {
		if (!chat) return;
		this.conversation.set(chat);

		if (goToLastDashboard && chat.turns.length > 0) {
			// Als de geladen chat een dashboard bevat, ga dan naar dat dashboard
			// We zoeken het laatste antwoord met een dashboard, omdat niet elk antwoord er een hoeft te bevatten
			// Bijvoorbeeld als de gebruiker een vervolgvraag stelt zonder dat er een dashboard voor nodig is
			const lastResponseWithDashboard = [...chat.turns].reverse().find((turn) => turn.response?.dashboard)?.response;
			const dashboard = lastResponseWithDashboard?.dashboard;
			this.goToDashboard(getDashboardURLComponents(dashboard));
		}

		this.focusTextarea();
	}
}
